import { useEffect, useState } from 'react';
import { api, type FinInstExpirePreview } from '../lib/api';
import { ErrorBox } from './DataTable';

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * 할부금 만기 구글챗 알림(게시판 #449) — 할부금 화면 「만기 알림」에서 연다(회계·관리자).
 * 매일 09:45, 회사 실납부 계약 중 만기 3일 전 ~ 당일인 것을 「할부만기」 스페이스에 카드로 올린다(옛 시트 알림과 같은 규칙).
 * 🔴 켜기 전에 오늘 몇 장·앞으로 며칠에 몇 장인지 먼저 본다. 켜기·지금 보내기는 관리자만(서버가 막는다).
 */
export default function FinInstExpirePanel({ isAdmin, onClose }: { isAdmin: boolean; onClose: () => void }) {
  const [data, setData] = useState<FinInstExpirePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.previewFinInstExpire().then((r) => { if (!cancelled) setData(r); }).catch((e) => { if (!cancelled) setError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [nonce]);

  async function toggle() {
    if (!data) return;
    setBusy('toggle'); setError(null); setNotice(null);
    try {
      const r = await api.setFinInstExpireEnabled({ enabled: !data.enabled });
      setNotice(r.enabled ? '켰습니다 — 매일 09:45 에 올립니다. 오늘분은 「지금 보내기」로 올릴 수 있습니다.' : '껐습니다 — 더 이상 올리지 않습니다.');
      setNonce((n) => n + 1);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  }
  async function runNow() {
    setBusy('run'); setError(null); setNotice(null);
    try {
      const r = await api.runFinInstExpireNow();
      setNotice(`올림 ${r.sent}장 · 실패 ${r.failed}장 · 건너뜀 ${r.skipped}장(오늘 이미 올림)`);
      setNonce((n) => n + 1);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  }

  const toSend = data?.rows.filter((r) => !r.sent).length ?? 0;
  const aheadTotal = data?.ahead.reduce((s, a) => s + a.cards, 0) ?? 0;
  const aheadMax = data?.ahead.reduce((m, a) => Math.max(m, a.cards), 0) ?? 0;
  return (
    <div className="card max-h-[85vh] space-y-3 overflow-auto p-5">
      <div className="flex items-start gap-3">
        <div>
          <h2 className="text-base font-bold">할부금 만기 알림(구글챗)</h2>
          <p className="mt-1 text-xs text-ink-soft">
            매일 09:45, 회사 실납부 계약 중 만기일이 <b>{data?.days ?? 3}일 전 ~ 당일</b>인 계약을 「할부만기」 스페이스에 카드로 올립니다
            (계약마다 하루 한 장 · 할부종료·중도상환한 계약은 빠집니다).
          </p>
        </div>
        <button className="btn-ghost ml-auto" data-modal-close onClick={onClose}>닫기</button>
      </div>
      {error && <ErrorBox message={error} />}
      {notice && <p className="rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{notice}</p>}
      {!data && !error && <p className="text-sm text-ink-faint">불러오는 중…</p>}
      {data && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className={`rounded-full px-2.5 py-0.5 font-semibold ${data.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-canvas text-ink-soft'}`}>
              자동 알림 {data.enabled ? '켜짐' : '꺼짐'}
            </span>
            {data.switchedAt && <span className="text-xs text-ink-faint">{data.switchedAt} {data.switchedBy?.split('@')[0]}</span>}
            <span className={`rounded-full px-2.5 py-0.5 text-xs ${data.webhookReady ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}>
              스페이스 연결 {data.webhookReady ? '됨' : '안 됨'}
            </span>
            {isAdmin && (
              <span className="ml-auto flex gap-2">
                <button className={data.enabled ? 'btn' : 'btn-primary'} disabled={!!busy} onClick={() => void toggle()}>
                  {busy === 'toggle' ? '바꾸는 중…' : data.enabled ? '자동 알림 끄기' : '자동 알림 켜기'}
                </button>
                <button className="btn" disabled={!!busy || !data.enabled || !data.webhookReady || toSend === 0} onClick={() => void runNow()}
                        title="오늘 올릴 카드를 지금 올립니다. 오늘 이미 올린 계약은 다시 가지 않습니다.">
                  {busy === 'run' ? '올리는 중…' : `지금 보내기 (${toSend}장)`}
                </button>
              </span>
            )}
          </div>

          <section>
            <h3 className="mb-1 text-sm font-semibold">오늘({data.day}) 카드 {data.rows.length}장 · 올릴 것 {toSend}장</h3>
            {data.rows.length === 0 ? <p className="text-xs text-ink-faint">오늘은 만기가 가까운 계약이 없습니다.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-ink-soft">
                  <th className="py-1">차량번호</th><th>성명</th><th>만기일</th><th>남은 날</th><th className="text-right">납입액</th><th>상태</th>
                </tr></thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.id} className="border-t border-line">
                      <td className="py-1">{r.label}</td><td>{r.holder ?? '—'}</td><td>{r.endDate}</td>
                      <td>{r.daysLeft === 0 ? '오늘' : `${r.daysLeft}일 전`}</td>
                      <td className="text-right tabular-nums">{r.amount || '—'}</td>
                      <td className={r.sent ? 'text-emerald-700' : 'text-ink-soft'}>{r.sent ? '올림' : '올릴 예정'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold">앞으로 60일 — 모두 {aheadTotal}장 · 하루 최대 {aheadMax}장</h3>
            <div className="flex flex-wrap gap-1 text-[11px]">
              {data.ahead.filter((a) => a.cards > 0).map((a) => (
                <span key={a.day} className="rounded bg-canvas px-1.5 py-0.5">{a.day.slice(5)} <b>{a.cards}</b></span>
              ))}
            </div>
          </section>

          {data.sample && (
            <details>
              <summary className="cursor-pointer text-sm font-semibold">카드 문구 예시</summary>
              <pre className="mt-1 whitespace-pre-wrap rounded bg-canvas p-3 text-xs">{`할부금 만기가 도래하였습니다.\n\n${data.sample}`}</pre>
            </details>
          )}

          <section>
            <h3 className="mb-1 text-sm font-semibold">최근 기록</h3>
            {data.recent.length === 0 ? <p className="text-xs text-ink-faint">아직 올린 적이 없습니다.</p> : (
              <table className="w-full text-xs">
                <tbody>
                  {data.recent.map((r) => (
                    <tr key={r.id} className="border-t border-line">
                      <td className="py-1">{r.at}</td><td>{r.label.replace(/^◆ 차량번호: /, '')}</td><td>{r.days_left}일 전</td>
                      <td className={r.status === 'sent' ? 'text-emerald-700' : 'text-red-600'} title={r.error ?? ''}>{r.status === 'sent' ? '올림' : '실패'}</td>
                      <td className="text-ink-faint">{r.created_by === 'schedule' ? '자동' : r.created_by.split('@')[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
